import { useWatch } from "react-hook-form";
import type { Control } from "react-hook-form";
import type { PRGM } from "@/models.ts";

function getProgramYearDateRange(
  programYear: string | undefined
): { start: string; end: string } | null {
  if (!programYear) return null;

  const match = programYear.match(/^(\d{4})-(\d{2,4})$/);
  if (!match) return null;

  const startYear = Number.parseInt(match[1], 10);
  const endYearSuffix = match[2];
  const endYear =
    endYearSuffix.length === 2
      ? Number.parseInt(`${Math.floor(startYear / 100)}${endYearSuffix}`, 10)
      : Number.parseInt(endYearSuffix, 10);

  return {
    start: `${startYear}-07-01`,
    end: `${endYear}-06-30`,
  };
}

function isWithinProgramYear(
  dateStr: string | undefined,
  programYear: string | undefined
): boolean {
  const range = getProgramYearDateRange(programYear);
  if (!range || !dateStr) return true;

  return dateStr >= range.start && dateStr <= range.end;
}

export function usePRGMFormLogic(control: Control<PRGM>, viewOnly = false) {
  const watchProgramYear = useWatch({ control, name: "ProgramYear" });
  const watchMembershipStart = useWatch({
    control,
    name: "EduProgramMembershipStartDate",
  });

  const validateMembershipStart = (value?: string) => {
    if (viewOnly || !value) return true;

    if (!isWithinProgramYear(value, watchProgramYear)) {
      return "Membership Start Date must be within the program year";
    }

    return true;
  };

  const validateMembershipEnd = (value?: string) => {
    if (viewOnly || !value) return true;

    if (watchMembershipStart && value < watchMembershipStart) {
      return "Membership End Date must be after Membership Start Date";
    }

    if (!isWithinProgramYear(value, watchProgramYear)) {
      return "Membership End Date must be within the program year";
    }

    return true;
  };

  return {
    watchProgramYear,
    validateMembershipStart,
    validateMembershipEnd,
  };
}
